import React from "react";
import { useSelector } from "react-redux";
import CustomerSimplifiedDTO from "./dtos/CustomerSimplifiedDTO";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { useHistory } from "react-router-dom";
import {
  SessionRoute,
  previousRouteKey,
} from "features/shared/models/SessionRoute";
import IPersistentLog from "infrastructure/logging/IPersistentLog";
import CustomerSelectSection from 'features/customer/sections/CustomerSelectSection';
import { useTranslation } from "react-i18next";
import SimpleLayout from "features/shared/layout/SimpleLayout";
import Header from "features/shared/layout/header/Header";
import ModuleContent from "features/shared/layout/content/ModuleContent";
import IAppState from "infrastructure/store/IAppState";
import { UserState } from "redux-oidc";
import CustomerSelectAdminHeader from "./components/CustomerSelectAdminHeader";
import { isAdminUser } from "infrastructure/oidc/oidcHelper";
import ICustomerService from "./services/ICustomerService";

type LocationState = {
  from: string;
};

const CustomerSelect = () => {
  // TRANSLATIONS
  const t = useTranslation("shared\\moduleNames").t;

  // DI
  const persistentLog = useInjection<IPersistentLog>(IocTypes.PersistentLog);
  const customerService = useInjection<ICustomerService>(IocTypes.CustomerService);

  // ROUTER
  const history = useHistory();

  // STORE
  const user = useSelector<IAppState, UserState>((store) => store.oidc).user;

  // HELPERS
  const changeToPreviousRoute = () => {
    if (history.location.state) {
      updateSessionRoute();

      let locationState = history.location.state as LocationState;
      history.push(locationState.from);
    } else {
      updateSessionRoute(true);
    }
  };

  const updateSessionRoute = (pushToHistory: boolean = false) => {
    const sessionRouteStr = sessionStorage.getItem(previousRouteKey);
    const sessionRoute: SessionRoute =
      sessionRouteStr != null ? JSON.parse(sessionRouteStr) : null;

    if (sessionRoute !== null && sessionRoute.isUsed === false) {
      sessionRoute.isUsed = true;
      sessionStorage.setItem(previousRouteKey, JSON.stringify(sessionRoute));

      pushToHistory && history.push(sessionRoute.route);
    }
  };

  // HANDLERS
  const selectCustomerHandler = async (customer: CustomerSimplifiedDTO) => {
    persistentLog.customerLogin(customer, isAdminUser(user));

    await customerService.loadAndStoreCustomerData(customer, user?.profile.role);

    changeToPreviousRoute();
  };

  // SUB-RENDERS
  const renderDataGridInWrapper = (dataGridRender: () => JSX.Element): JSX.Element => {
    return (
      <SimpleLayout useSpinner>
        {renderHeader()}
        <ModuleContent>{dataGridRender()}</ModuleContent>
      </SimpleLayout>
    );
  }

  const renderHeader = () => { 
    if (isAdminUser(user)) {
      return <CustomerSelectAdminHeader />;
    } else {
      return (
        <Header
            title={t("customerSelect.title")}
            subtitle={t("customerSelect.subtitle")}
          />
      );
    }
  }

  // RENDER 
  return (
    <CustomerSelectSection 
      automaticSelectCustomer={true}
      onCustomerClick={selectCustomerHandler}
      renderDataGridWrapper={renderDataGridInWrapper}
    />
  )
};

export default CustomerSelect;